import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Search, 
  TrendingUp, 
  Calendar, 
  Star, 
  Users,
  Bell,
  Menu,
  User,
  LogOut,
  Settings,
  Upload,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArchetypeMascot } from '@/components/ArchetypeMascot';
import { MinorAccountBanner } from '@/components/MinorAccountBanner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ReviewCard } from '@/components/ReviewCard';
import { ActivityCard } from '@/components/ActivityCard';
import { ReadingGoalDashboardWidget } from '@/components/ReadingGoalDashboardWidget';
import { StreakSystemMigrationModal } from '@/components/StreakSystemMigrationModal';
import { FeedFilter } from '@/components/FeedFilter';
import { FeedFilterType, FeedSortType, FeedItem } from '@/types/feedFilters';
import { useCheckAchievements } from '@/hooks/useAchievements';
import { useQuestTracking } from '@/hooks/useQuestTracking';
import { useStreakMigration } from '@/hooks/useStreakMigration';
import { useUserBlocks } from '@/hooks/useUserBlocks';
import { LevelAndFocusCard } from '@/components/LevelAndFocusCard';
import { SuccessCelebration } from '@/components/SuccessCelebration';
import { FeedErrorBoundary } from '@/components/FeedErrorBoundary';

interface Activity {
  id: string;
  type: string;
  payload: any;
  created_at: string;
  book_title?: string;
  book_cover_url?: string;
  user_id: string;
  book_id?: string;
  cheerCount?: number;
  profiles?: {
    username?: string;
    name?: string;
    image?: string;
  };
}

interface Review {
  id: string;
  book_id: string;
  user_id: string;
  rating_star: number;
  review_text?: string;
  created_at: string;
  privacy: string;
  books?: {
    id: string;
    title: string;
    authors: string[];
    cover_url?: string;
  } | null;
  profiles?: {
    id: string;
    name?: string;
    username?: string;
    image?: string;
    is_private_name?: boolean;
  } | null;
}


export default function Home() {
  const { user, signOut, loading: authLoading } = useAuth();
  const checkAchievements = useCheckAchievements();
  useQuestTracking(); // Automatically track quest progress
  const { shouldShowModal, dismissModal, loading: migrationLoading } = useStreakMigration();
  const { blockedUsers } = useUserBlocks();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<{ username?: string; archetype?: string } | null>(null);
  const [feedFilter, setFeedFilter] = useState<FeedFilterType>('all');
  const [feedSort, setFeedSort] = useState<FeedSortType>('recent');
  const [feedPage, setFeedPage] = useState(1);
  const [celebration, setCelebration] = useState<{ type: 'level_up' | 'goal_complete', title: string, description?: string, level?: number } | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check for new achievements when component mounts
  useEffect(() => {
    if (user?.id) {
      checkAchievements.mutate(user.id);
    }
  }, [user?.id]);

  // Auth guard - wait for auth to load before redirecting
  useEffect(() => {
    if (authLoading) return; // Wait for auth check to complete
    if (!user) {
      navigate('/auth');
      return;
    }
    
    checkProfileAndLoadData();
  }, [user?.id, authLoading]); // Include authLoading in dependencies

  // Set up real-time subscriptions for feed updates with debouncing
  useEffect(() => {
    if (!user) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    
    const debouncedReload = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadDashboardData();
      }, 1000); // Wait 1 second before reloading to batch rapid updates
    };

    const channel = supabase
      .channel('feed-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'activities'
        },
        (payload) => {
          console.log('Activity update:', payload);
          debouncedReload();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_books'
        },
        (payload) => {
          console.log('User book update:', payload);
          debouncedReload();
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const checkProfileAndLoadData = async () => {
    if (!user) return;

    try {
      // Check if user has a profile; if not, still allow access but log for debugging
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username, archetype')
        .eq('id', user.id)
        .maybeSingle();

      if (!profileData?.username) {
        console.warn('Home: user profile missing username, but allowing access to avoid onboarding loops', {
          userId: user.id,
          profileData,
        });
      }

      setUserProfile(profileData);
      await loadDashboardData();
    } catch (error) {
      console.error('Profile check error:', error);
    }
  };

  const loadDashboardData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch list of users being followed
      const { data: followingData } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .eq('status', 'accepted');

      const followingIds = followingData?.map(f => f.following_id) || [];

      // Build feed: combine my ratings + followed users' public ratings, then enrich
      const { data: myRatings } = await supabase
        .from('user_books')
        .select('id, book_id, user_id, rating_star, review_text, created_at')
        .eq('user_id', user.id)
        .not('rating_star', 'is', null)
        .order('created_at', { ascending: false })
        .limit(25);

      let followedRatings: any[] = [];
      if (followingIds.length > 0) {
        const { data } = await supabase
          .from('user_books')
          .select('id, book_id, user_id, rating_star, review_text, created_at, privacy')
          .in('user_id', followingIds)
          .not('rating_star', 'is', null)
          .order('created_at', { ascending: false })
          .limit(100);
        followedRatings = data || [];
      }

      const combined = [ ...(myRatings || []), ...followedRatings ]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 50);

      const bookIds = Array.from(new Set(combined.map(r => r.book_id).filter(Boolean)));
      const userIds = Array.from(new Set(combined.map(r => r.user_id)));

      const [{ data: booksData }, { data: profilesData }] = await Promise.all([
        bookIds.length > 0
          ? supabase.from('books').select('id, title, authors, cover_url, cover_cache_url').in('id', bookIds)
          : Promise.resolve({ data: [] as any }),
        userIds.length > 0
          ? supabase.rpc('get_profiles_by_ids', { user_ids: userIds })
          : Promise.resolve({ data: [] as any })
      ]);

      const booksMap = new Map<string, any>();
      (booksData || []).forEach((b: any) => booksMap.set(b.id, b));
      const profilesMap = new Map<string, any>();
      (profilesData || []).forEach((p: any) => profilesMap.set(p.id, p));

      const reviewsMerged = combined.map((r: any) => ({
        ...r,
        books: booksMap.get(r.book_id) || null,
        profiles: profilesMap.get(r.user_id) || null,
      }));

      // Filter out reviews from blocked users
      const blockedUserIds = new Set(blockedUsers.map((b: any) => b.blocked_id));
      const filteredReviews = reviewsMerged.filter((r: any) => !blockedUserIds.has(r.user_id));

      setReviews(filteredReviews as Review[]);

      // Fetch activities from followed users AND own activities
      const { data: followedActivitiesData } = await supabase
        .from('activities')
        .select(`
          id,
          type,
          payload,
          created_at,
          book_title,
          book_cover_url,
          user_id,
          book_id
        `)
        .in('user_id', followingIds.length > 0 ? followingIds : [user.id])
        .order('created_at', { ascending: false })
        .limit(50);

      // Fetch user's own activities
      const { data: ownActivitiesData } = await supabase
        .from('activities')
        .select(`
          id,
          type,
          payload,
          created_at,
          book_title,
          book_cover_url,
          user_id,
          book_id
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      // Combine both activity sets and remove duplicates
      const allActivities = [...(followedActivitiesData || []), ...(ownActivitiesData || [])];
      const deduplicatedActivities = Array.from(
        new Map(allActivities.map(item => [item.id, item])).values()
      );

      // Filter to only show specific activity types
      const allowedActivityTypes = [
        'focus_session_completed',
        'level_up'
      ];
      const uniqueActivities = deduplicatedActivities.filter(
        activity => allowedActivityTypes.includes(activity.type)
      );

      if (uniqueActivities.length > 0) {
        // Get unique user IDs from activities
        const userIds = [...new Set(uniqueActivities.map(activity => activity.user_id))];
        
        // Fetch profile data for all users in the activities using secure function
        const { data: profilesData } = await supabase
          .rpc('get_profiles_by_ids', { user_ids: userIds });

        // Create a map of user profiles for quick lookup
        const profilesMap = new Map();
        if (profilesData) {
          profilesData.forEach(profile => {
            profilesMap.set(profile.id, profile);
          });
        }

        // Fetch cheer counts for all activities (for popular sorting)
        const allActivityIds = uniqueActivities.map(a => a.id);
        const { data: cheerCounts } = await supabase
          .from('activity_reactions')
          .select('activity_id')
          .in('activity_id', allActivityIds);

        // Create a map of activity_id -> cheer count
        const cheerCountMap = new Map<string, number>();
        cheerCounts?.forEach(cheer => {
          cheerCountMap.set(cheer.activity_id, (cheerCountMap.get(cheer.activity_id) || 0) + 1);
        });

        // Combine activities with profile data and cheer counts
        const activitiesWithProfiles = uniqueActivities.map(activity => ({
          ...activity,
          profiles: profilesMap.get(activity.user_id) || null,
          cheerCount: cheerCountMap.get(activity.id) || 0,
        }));

        // Filter out activities from blocked users
        const blockedUserIds = new Set(blockedUsers.map((b: any) => b.blocked_id));
        const filteredActivities = activitiesWithProfiles.filter(a => !blockedUserIds.has(a.user_id));

        setActivities(filteredActivities);
      } else {
        setActivities([]);
      }

      // Load current user's profile for header
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username, archetype')
        .eq('id', user.id)
        .maybeSingle();

      if (profileData) {
        setUserProfile(profileData);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        variant: "destructive",
        title: "Error loading data",
        description: "There was a problem loading your dashboard data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  // Filter feed items based on selected filter
  const filterFeedItems = (items: FeedItem[], filter: FeedFilterType): FeedItem[] => {
    switch (filter) {
      case 'all':
        return items;
      case 'reviews':
        return items.filter(item => {
          if (item.type === 'review') {
            return item.data.review_text && item.data.review_text.length > 0;
          }
          return false;
        });
      case 'ratings':
        return items.filter(item => {
          if (item.type === 'review') {
            return item.data.rating_star && (!item.data.review_text || item.data.review_text.length === 0);
          }
          if (item.type === 'activity') {
            return ['rating_created', 'rating_updated'].includes(item.data.type);
          }
          return false;
        });
      case 'milestones':
        return items.filter(item => {
          if (item.type === 'activity') {
            return ['book_finished', 'level_up', 'streak_milestone'].includes(item.data.type);
          }
          return false;
        });
      case 'reading':
        return items.filter(item => {
          if (item.type === 'activity') {
            return ['book_started', 'reading_progress_updated', 'focus_session_completed', 'focus_session_started'].includes(item.data.type);
          }
          return false;
        });
      case 'finished':
        return items.filter(item => {
          if (item.type === 'activity') {
            return item.data.type === 'book_finished';
          }
          return false;
        });
      default:
        return items;
    }
  };

  // Sort feed items based on selected sort
  const sortFeedItems = (items: FeedItem[], sort: FeedSortType): FeedItem[] => {
    switch (sort) {
      case 'recent':
        return [...items].sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case 'popular':
        return [...items].sort((a, b) => 
          (b.cheerCount || 0) - (a.cheerCount || 0)
        );
      default:
        return items;
    }
  };

  // Get empty state message based on active filter
  const getEmptyStateMessage = (filter: FeedFilterType): string => {
    switch (filter) {
      case 'reviews':
        return 'No reviews yet';
      case 'milestones':
        return 'No milestones to celebrate yet';
      case 'reading':
        return 'No reading updates';
      case 'finished':
        return 'No finished books yet';
      case 'ratings':
        return 'No ratings yet';
      default:
        return 'Your reading journey starts here!';
    }
  };

  // Get empty state description based on active filter
  const getEmptyStateDescription = (filter: FeedFilterType): string => {
    switch (filter) {
      case 'reviews':
        return 'Write a review or follow readers who share their thoughts on books.';
      case 'milestones':
        return 'Finish books, level up, or build streaks to see milestones here.';
      case 'reading':
        return 'Start reading books or update your progress to see activity here.';
      case 'finished':
        return 'Mark books as finished to see them celebrated here.';
      case 'ratings':
        return 'Rate books or follow readers who rate books.';
      default:
        return 'Rate books, follow readers, or start reading to build your feed.';
    }
  };

  // Deduplicate activities that are already represented by review records
  const deduplicateFeed = (reviews: Review[], activities: Activity[]): Activity[] => {
    // Create a set of user_id+book_id combinations from reviews for quick lookup
    const reviewKeys = new Set(
      reviews.map(review => `${review.user_id}_${review.book_id}`)
    );

    const nonRedundantTypes = new Set([
      'focus_session_completed',
      'reading_progress_updated',
      'level_up',
      'streak_milestone',
      'review_liked',
      'review_commented',
      'book_dnf',
    ]);
    
    // Filter out activities that are duplicates of reviews
    return activities.filter(activity => {
      const key = activity.book_id ? `${activity.user_id}_${activity.book_id}` : '';
      const hasReview = key ? reviewKeys.has(key) : false;

      // Any rating/review activity should be removed if a review exists
      if ((activity.type.startsWith('rating_') || activity.type === 'review_created') && hasReview) {
        return false;
      }

      // Also hide lightweight actions like book_saved when a review exists for the same book
      if (activity.type === 'book_saved' && hasReview) {
        return false;
      }

      // For any unknown activity types tied to the same book, hide if a review exists
      if (!nonRedundantTypes.has(activity.type) && hasReview) {
        return false;
      }

      // Keep all other activity types
      return true;
    });
  };

  const deduplicatedActivities = useMemo(() => 
    deduplicateFeed(reviews, activities), 
    [reviews, activities]
  );

  // Create unified feed items from reviews and deduplicated activities - memoized
  const unifiedFeed = useMemo<FeedItem[]>(() => [
    ...reviews.map(review => ({
      id: review.id,
      type: 'review' as const,
      created_at: review.created_at,
      data: review,
      cheerCount: 0, // Reviews don't have cheer counts currently
    })),
    ...deduplicatedActivities.map(activity => ({
      id: activity.id,
      type: 'activity' as const,
      created_at: activity.created_at,
      data: activity,
      cheerCount: activity.cheerCount || 0,
    })),
  ], [reviews, deduplicatedActivities]);

  // Apply filters and sorting - memoized
  const filteredFeed = useMemo(() => 
    filterFeedItems(unifiedFeed, feedFilter), 
    [unifiedFeed, feedFilter]
  );
  
  const sortedFeed = useMemo(() => 
    sortFeedItems(filteredFeed, feedSort), 
    [filteredFeed, feedSort]
  );
  
  // Pagination - memoized
  const ITEMS_PER_PAGE = 20;
  const paginatedFeed = useMemo(() => 
    sortedFeed.slice(0, feedPage * ITEMS_PER_PAGE), 
    [sortedFeed, feedPage]
  );

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Subtle background gradient */}
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'var(--gradient-mesh)' }} />
      
      <main className="container mx-auto px-4 py-8 space-y-6 relative z-10">
        {/* Welcome Section */}
        <section className="flex items-center gap-4 animate-fade-in">
          {userProfile?.archetype && (
            <ArchetypeMascot archetype={userProfile.archetype as any} size="lg" />
          )}
          <div className="flex-1">
            <h1 className="text-4xl font-heading font-bold bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, reader! ✨
            </h1>
            <p className="text-muted-foreground text-lg font-accent mt-2">
              What adventures will you have with Dino today?
            </p>
          </div>
        </section>

        {/* Minor Account Banner */}
        <MinorAccountBanner />

        {/* Reading Goal & Level Cards */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReadingGoalDashboardWidget 
            onClick={() => navigate('/wrapped')}
            onGoalComplete={(booksRead) => {
              setCelebration({
                type: 'goal_complete',
                title: '🎯 Goal Achieved!',
                description: `You've read ${booksRead} books this year! Amazing achievement!`
              });
            }}
          />
          <LevelAndFocusCard
            onLevelUp={(level, title) => {
              setCelebration({
                type: 'level_up',
                title: '🎉 Level Up!',
                description: title,
                level
              });
            }}
          />
         </section>

        {/* Activity Feed */}
        <section className="space-y-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h2 className="text-2xl font-heading font-bold bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">Activity Feed</h2>
            <FeedFilter
              currentFilter={feedFilter}
              currentSort={feedSort}
              onFilterChange={setFeedFilter}
              onSortChange={setFeedSort}
              itemCount={sortedFeed.length}
            />
          </div>
          
          <FeedErrorBoundary fallbackMessage="Couldn't load your feed">
            {paginatedFeed.length > 0 ? (
              <>
                <div className="space-y-6">
                  {paginatedFeed.map((feedItem) => {
                    if (feedItem.type === 'review') {
                      return (
                        <ReviewCard 
                          key={feedItem.id} 
                          review={feedItem.data}
                          showBookInfo={true}
                        />
                      );
                    } else {
                      return (
                        <ActivityCard 
                          key={feedItem.id} 
                          activity={feedItem.data}
                        />
                      );
                    }
                  })}
                </div>
                
                {sortedFeed.length > feedPage * ITEMS_PER_PAGE && (
                  <div className="flex justify-center pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setFeedPage(prev => prev + 1)}
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Card className="card-gradient">
                <CardContent className="p-8 text-center">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 rounded-full bg-secondary/20 blur-2xl animate-pulse" />
                    <div className="relative rounded-full bg-gradient-to-br from-secondary/20 to-accent/20 p-6 mx-auto w-fit animate-float">
                      <Users className="w-12 h-12 text-secondary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-2">
                    {getEmptyStateMessage(feedFilter)}
                  </h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {getEmptyStateDescription(feedFilter)}
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Button asChild>
                      <Link to="/search">
                        <BookOpen className="w-4 h-4 mr-2" />
                        Find Books
                      </Link>
                    </Button>
                    <Button asChild variant="outline">
                      <Link to="/search?tab=people">
                        <Users className="w-4 h-4 mr-2" />
                        Find People
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </FeedErrorBoundary>
        </section>
      </main>

      {/* Streak System Migration Modal */}
      <StreakSystemMigrationModal 
        open={shouldShowModal} 
        onClose={dismissModal}
      />

      {/* Success Celebration */}
      {celebration && (
        <SuccessCelebration
          isOpen={!!celebration}
          onClose={() => setCelebration(null)}
          type={celebration.type}
          title={celebration.title}
          description={celebration.description}
          level={celebration.level}
        />
      )}
    </div>
  );
}